export type Recommendation = "safe" | "compare" | "high-risk" | "avoid";

export interface Lender {
  slug: string;
  name: string;
  category: string;
  founded: number;
  hq: string;
  loanTypes: string[];
  aprRange: [number, number];
  amountRange: [number, number];
  termRange: string;
  scores: {
    trust: number;
    predatory: number; // higher = more predatory
    sentiment: number;
    complaints: number; // higher = worse
    feeTransparency: number;
  };
  recommendation: Recommendation;
  summary: string;
  redFlags: string[];
  greenFlags: string[];
  reviewCount: number;
  complaintBreakdown: { category: string; count: number }[];
  sentimentTrend: { month: string; positive: number; negative: number }[];
  sampleReviews: { author: string; rating: number; text: string; date: string }[];
}

const trend = (a: number, b: number) => [
  { month: "Jan", positive: a, negative: b },
  { month: "Feb", positive: a + 2, negative: b - 1 },
  { month: "Mar", positive: a - 3, negative: b + 4 },
  { month: "Apr", positive: a + 5, negative: b - 2 },
  { month: "May", positive: a + 1, negative: b + 1 },
  { month: "Jun", positive: a + 4, negative: b - 3 },
];

export const lenders: Lender[] = [
  {
    slug: "opploans",
    name: "OppLoans",
    category: "Installment Lender",
    founded: 2012,
    hq: "Chicago, IL",
    loanTypes: ["Installment"],
    aprRange: [59, 160],
    amountRange: [500, 4000],
    termRange: "9–18 months",
    scores: { trust: 42, predatory: 78, sentiment: 38, complaints: 71, feeTransparency: 55 },
    recommendation: "high-risk",
    summary:
      "Targets subprime borrowers with triple-digit APRs. Marketing emphasizes speed; true cost of borrowing is significantly higher than mainstream personal loans.",
    redFlags: [
      "APR consistently above 100% in most states",
      "31% of negative reviews mention surprise fees",
      "Automatic withdrawals reported as difficult to cancel",
      "Loan amounts and terms designed to maximize interest",
    ],
    greenFlags: ["Reports to all three credit bureaus", "No prepayment penalty"],
    reviewCount: 4821,
    complaintBreakdown: [
      { category: "Hidden Fees", count: 412 },
      { category: "Unauthorized Withdrawals", count: 287 },
      { category: "Collections Pressure", count: 198 },
      { category: "Misleading APR", count: 341 },
      { category: "Customer Service", count: 156 },
    ],
    sentimentTrend: trend(28, 52),
    sampleReviews: [
      { author: "Marcus T.", rating: 1, text: "APR ended up being 160%. I paid back nearly triple what I borrowed.", date: "2025-04-12" },
      { author: "Jess R.", rating: 2, text: "Got the money fast but the fees kept stacking up.", date: "2025-03-08" },
    ],
  },
  {
    slug: "upstart",
    name: "Upstart",
    category: "AI Personal Loans",
    founded: 2012,
    hq: "San Mateo, CA",
    loanTypes: ["Personal", "Auto Refi"],
    aprRange: [7.8, 35.99],
    amountRange: [1000, 50000],
    termRange: "36–60 months",
    scores: { trust: 78, predatory: 22, sentiment: 71, complaints: 28, feeTransparency: 82 },
    recommendation: "safe",
    summary:
      "Uses AI underwriting to approve borrowers traditional models reject. APRs are competitive in the prime/near-prime range. Origination fees apply.",
    redFlags: ["Origination fee up to 12%", "Limited customer support hours"],
    greenFlags: [
      "Transparent APR disclosure",
      "Pre-qualification with soft credit pull",
      "No prepayment penalty",
      "Reports to credit bureaus",
    ],
    reviewCount: 9214,
    complaintBreakdown: [
      { category: "Origination Fee", count: 124 },
      { category: "Approval Delays", count: 88 },
      { category: "Customer Service", count: 67 },
      { category: "Loan Servicing", count: 41 },
    ],
    sentimentTrend: trend(58, 22),
    sampleReviews: [
      { author: "Dana K.", rating: 5, text: "Approved despite thin credit. APR was reasonable and process was clear.", date: "2025-05-21" },
      { author: "Will P.", rating: 3, text: "Origination fee was higher than expected. Otherwise fine.", date: "2025-04-02" },
    ],
  },
  {
    slug: "sofi",
    name: "SoFi",
    category: "Online Bank",
    founded: 2011,
    hq: "San Francisco, CA",
    loanTypes: ["Personal", "Student Refi", "Mortgage"],
    aprRange: [8.99, 29.99],
    amountRange: [5000, 100000],
    termRange: "24–84 months",
    scores: { trust: 88, predatory: 12, sentiment: 81, complaints: 18, feeTransparency: 92 },
    recommendation: "safe",
    summary:
      "Full-service online bank with strong reputation for personal loans. No fees, member benefits, and competitive APRs for borrowers with good credit.",
    redFlags: ["Requires good credit (typically 680+)"],
    greenFlags: [
      "No origination fees",
      "No prepayment penalties",
      "Unemployment protection program",
      "Rate discount with autopay",
    ],
    reviewCount: 12480,
    complaintBreakdown: [
      { category: "Approval Criteria", count: 92 },
      { category: "Customer Service", count: 71 },
      { category: "Application UX", count: 38 },
    ],
    sentimentTrend: trend(68, 14),
    sampleReviews: [
      { author: "Priya S.", rating: 5, text: "Smoothest loan process I've ever had. No surprise fees.", date: "2025-06-01" },
    ],
  },
  {
    slug: "lendingclub",
    name: "LendingClub",
    category: "Personal Loans",
    founded: 2006,
    hq: "San Francisco, CA",
    loanTypes: ["Personal", "Debt Consolidation"],
    aprRange: [8.98, 35.99],
    amountRange: [1000, 40000],
    termRange: "24–60 months",
    scores: { trust: 74, predatory: 28, sentiment: 64, complaints: 34, feeTransparency: 76 },
    recommendation: "compare",
    summary:
      "Established personal loan provider. Origination fees can be substantial. Good fit for debt consolidation if the rate beats your existing debt.",
    redFlags: ["Origination fee 3–8% deducted up front", "APR jumps quickly for fair credit"],
    greenFlags: ["Joint applications allowed", "Direct payment to creditors for consolidation"],
    reviewCount: 7390,
    complaintBreakdown: [
      { category: "Origination Fee", count: 218 },
      { category: "APR Disclosure", count: 142 },
      { category: "Servicing", count: 99 },
      { category: "Late Fees", count: 64 },
    ],
    sentimentTrend: trend(48, 28),
    sampleReviews: [
      { author: "Tom H.", rating: 4, text: "Consolidated four cards into one payment. Saved real money.", date: "2025-05-12" },
    ],
  },
  {
    slug: "avant",
    name: "Avant",
    category: "Near-Prime Personal Loans",
    founded: 2012,
    hq: "Chicago, IL",
    loanTypes: ["Personal"],
    aprRange: [9.95, 35.99],
    amountRange: [2000, 35000],
    termRange: "24–60 months",
    scores: { trust: 66, predatory: 38, sentiment: 58, complaints: 42, feeTransparency: 68 },
    recommendation: "compare",
    summary:
      "Built for fair-credit borrowers. APRs land on the high side; administration fee deducted from proceeds.",
    redFlags: ["4.75% administration fee", "Late fee structure unclear"],
    greenFlags: ["Accepts credit scores from 580", "Funds next business day"],
    reviewCount: 5102,
    complaintBreakdown: [
      { category: "Fees", count: 188 },
      { category: "APR", count: 132 },
      { category: "Servicing", count: 84 },
    ],
    sentimentTrend: trend(40, 32),
    sampleReviews: [],
  },
  {
    slug: "ace-cash-express",
    name: "ACE Cash Express",
    category: "Payday Lender",
    founded: 1968,
    hq: "Irving, TX",
    loanTypes: ["Payday", "Installment", "Title"],
    aprRange: [199, 661],
    amountRange: [100, 2000],
    termRange: "14 days – 12 months",
    scores: { trust: 22, predatory: 94, sentiment: 21, complaints: 88, feeTransparency: 28 },
    recommendation: "avoid",
    summary:
      "Storefront and online payday lender with extreme APRs. Heavy regulatory history, including CFPB enforcement actions.",
    redFlags: [
      "APR can exceed 600%",
      "Documented CFPB enforcement for unfair practices",
      "Rollover loans trap borrowers in debt cycle",
      "Aggressive collections reported",
    ],
    greenFlags: [],
    reviewCount: 3180,
    complaintBreakdown: [
      { category: "Debt Trap / Rollovers", count: 514 },
      { category: "Collections Harassment", count: 388 },
      { category: "APR Disclosure", count: 421 },
      { category: "Unauthorized Withdrawals", count: 261 },
    ],
    sentimentTrend: trend(12, 68),
    sampleReviews: [
      { author: "Anon", rating: 1, text: "Borrowed $400, paid back almost $1,200 over 4 months.", date: "2025-02-19" },
    ],
  },
  {
    slug: "speedy-cash",
    name: "Speedy Cash",
    category: "Payday Lender",
    founded: 1997,
    hq: "Wichita, KS",
    loanTypes: ["Payday", "Title", "Line of Credit"],
    aprRange: [200, 700],
    amountRange: [100, 26000],
    termRange: "Varies",
    scores: { trust: 26, predatory: 91, sentiment: 24, complaints: 84, feeTransparency: 32 },
    recommendation: "avoid",
    summary: "High-cost short-term lender. Title loan products put your vehicle at risk.",
    redFlags: ["Title loans risk vehicle repossession", "APRs routinely above 400%", "Rollovers common"],
    greenFlags: [],
    reviewCount: 2104,
    complaintBreakdown: [
      { category: "APR", count: 312 },
      { category: "Rollovers", count: 244 },
      { category: "Collections", count: 198 },
    ],
    sentimentTrend: trend(14, 60),
    sampleReviews: [],
  },
  {
    slug: "marcus",
    name: "Marcus by Goldman Sachs",
    category: "Bank Personal Loans",
    founded: 2016,
    hq: "New York, NY",
    loanTypes: ["Personal"],
    aprRange: [6.99, 24.99],
    amountRange: [3500, 40000],
    termRange: "36–72 months",
    scores: { trust: 90, predatory: 8, sentiment: 84, complaints: 14, feeTransparency: 95 },
    recommendation: "safe",
    summary: "Goldman Sachs-backed consumer lending. No fees of any kind. Strong borrower for prime credit.",
    redFlags: ["Requires good-to-excellent credit"],
    greenFlags: ["No origination, late, or prepayment fees", "On-time payment reward", "Transparent rates"],
    reviewCount: 8460,
    complaintBreakdown: [
      { category: "Approval Criteria", count: 64 },
      { category: "Customer Service", count: 38 },
    ],
    sentimentTrend: trend(72, 11),
    sampleReviews: [],
  },
  {
    slug: "lightstream",
    name: "LightStream",
    category: "Bank Personal Loans",
    founded: 2013,
    hq: "San Diego, CA",
    loanTypes: ["Personal", "Auto"],
    aprRange: [7.49, 25.49],
    amountRange: [5000, 100000],
    termRange: "24–84 months",
    scores: { trust: 89, predatory: 10, sentiment: 82, complaints: 16, feeTransparency: 94 },
    recommendation: "safe",
    summary: "Truist-owned online lender for excellent credit. Rate Beat Program and no fees.",
    redFlags: ["Excellent credit required (typically 720+)"],
    greenFlags: ["No fees", "Same-day funding possible", "Rate Beat Program"],
    reviewCount: 6210,
    complaintBreakdown: [{ category: "Approval Criteria", count: 71 }],
    sentimentTrend: trend(70, 13),
    sampleReviews: [],
  },
  {
    slug: "rise-credit",
    name: "Rise Credit",
    category: "Subprime Installment",
    founded: 2014,
    hq: "Fort Worth, TX",
    loanTypes: ["Installment"],
    aprRange: [60, 299],
    amountRange: [500, 5000],
    termRange: "4–26 months",
    scores: { trust: 38, predatory: 82, sentiment: 34, complaints: 74, feeTransparency: 48 },
    recommendation: "high-risk",
    summary: "Subprime installment lender. APRs decrease after a few on-time payments but still extreme.",
    redFlags: ["APRs above 200% in some states", "Marketing minimizes true cost"],
    greenFlags: ["Rate reductions for on-time payments"],
    reviewCount: 2940,
    complaintBreakdown: [
      { category: "APR", count: 312 },
      { category: "Hidden Fees", count: 188 },
      { category: "Collections", count: 142 },
    ],
    sentimentTrend: trend(24, 50),
    sampleReviews: [],
  },
  {
    slug: "oneMain",
    name: "OneMain Financial",
    category: "Secured Personal Loans",
    founded: 1912,
    hq: "Evansville, IN",
    loanTypes: ["Personal", "Auto-Secured"],
    aprRange: [18, 35.99],
    amountRange: [1500, 20000],
    termRange: "24–60 months",
    scores: { trust: 60, predatory: 44, sentiment: 54, complaints: 46, feeTransparency: 62 },
    recommendation: "compare",
    summary: "Branch-based lender for fair credit. Often requires collateral. APRs start high.",
    redFlags: ["Collateral requirement common", "Origination fees vary by state"],
    greenFlags: ["In-person service available", "Quick decisions"],
    reviewCount: 6820,
    complaintBreakdown: [
      { category: "Collateral", count: 188 },
      { category: "Fees", count: 144 },
      { category: "Servicing", count: 108 },
    ],
    sentimentTrend: trend(42, 30),
    sampleReviews: [],
  },
  {
    slug: "best-egg",
    name: "Best Egg",
    category: "Personal Loans",
    founded: 2014,
    hq: "Wilmington, DE",
    loanTypes: ["Personal"],
    aprRange: [8.99, 35.99],
    amountRange: [2000, 50000],
    termRange: "36–60 months",
    scores: { trust: 76, predatory: 26, sentiment: 70, complaints: 30, feeTransparency: 78 },
    recommendation: "compare",
    summary: "Solid personal loan option for prime borrowers. Origination fee applies.",
    redFlags: ["Origination fee 0.99–8.99%"],
    greenFlags: ["Fast funding", "Pre-qualification with soft pull"],
    reviewCount: 5430,
    complaintBreakdown: [{ category: "Origination Fee", count: 158 }],
    sentimentTrend: trend(54, 22),
    sampleReviews: [],
  },
  {
    slug: "discover-personal",
    name: "Discover Personal Loans",
    category: "Bank Personal Loans",
    founded: 2007,
    hq: "Riverwoods, IL",
    loanTypes: ["Personal", "Debt Consolidation"],
    aprRange: [7.99, 24.99],
    amountRange: [2500, 40000],
    termRange: "36–84 months",
    scores: { trust: 86, predatory: 12, sentiment: 79, complaints: 19, feeTransparency: 93 },
    recommendation: "safe",
    summary: "Major bank lender with no fees and transparent terms. Strong for debt consolidation.",
    redFlags: ["Requires 660+ FICO typically"],
    greenFlags: ["No origination fees", "Direct creditor payments for consolidation", "30-day return guarantee"],
    reviewCount: 9120,
    complaintBreakdown: [{ category: "Approval", count: 78 }, { category: "Servicing", count: 41 }],
    sentimentTrend: trend(66, 16),
    sampleReviews: [],
  },
  {
    slug: "check-into-cash",
    name: "Check Into Cash",
    category: "Payday Lender",
    founded: 1993,
    hq: "Cleveland, TN",
    loanTypes: ["Payday", "Title", "Installment"],
    aprRange: [200, 780],
    amountRange: [50, 1500],
    termRange: "14–30 days",
    scores: { trust: 24, predatory: 92, sentiment: 22, complaints: 86, feeTransparency: 30 },
    recommendation: "avoid",
    summary: "Classic storefront payday lender. APRs in the triple digits with rollover risk.",
    redFlags: ["APRs commonly above 400%", "Short term creates rollover pressure"],
    greenFlags: [],
    reviewCount: 1840,
    complaintBreakdown: [
      { category: "APR", count: 244 },
      { category: "Rollovers", count: 188 },
    ],
    sentimentTrend: trend(13, 62),
    sampleReviews: [],
  },
  {
    slug: "happy-money",
    name: "Happy Money",
    category: "Credit Card Payoff",
    founded: 2009,
    hq: "Tustin, CA",
    loanTypes: ["Personal", "Debt Consolidation"],
    aprRange: [11.72, 24.5],
    amountRange: [5000, 40000],
    termRange: "24–60 months",
    scores: { trust: 80, predatory: 18, sentiment: 75, complaints: 22, feeTransparency: 84 },
    recommendation: "safe",
    summary: "Focused on paying off credit card debt. Caps APR below industry max.",
    redFlags: ["Origination fee 1.5–5.5%", "Personal loans only for credit card refi"],
    greenFlags: ["APR cap below 25%", "Soft pull pre-qual"],
    reviewCount: 4120,
    complaintBreakdown: [{ category: "Origination Fee", count: 88 }],
    sentimentTrend: trend(60, 18),
    sampleReviews: [],
  },
];

export const findLender = (slug: string) => lenders.find((l) => l.slug === slug);

export const recommendationMeta: Record<Recommendation, { label: string; tone: string; bg: string }> = {
  safe: { label: "Safe to Consider", tone: "text-success", bg: "bg-success/10 border-success/30" },
  compare: { label: "Compare Carefully", tone: "text-foreground", bg: "bg-warning/10 border-warning/40" },
  "high-risk": { label: "High Risk", tone: "text-warning-foreground", bg: "bg-warning/20 border-warning/60" },
  avoid: { label: "Avoid", tone: "text-danger", bg: "bg-danger/10 border-danger/40" },
};

// Financial health mock
export const userFinancials = {
  name: "Alex Morgan",
  creditScore: 648,
  financialWellness: 62,
  loanReadiness: 54,
  monthlyIncome: 4800,
  monthlyExpenses: 3950,
  totalDebt: 14200,
  creditUtilization: 78,
  emergencySavings: 320,
  onTimePaymentRate: 92,
};

export const savedSearches = ["OppLoans", "Upstart", "SoFi"];
export const savedOffers = [
  { lender: "OppLoans", amount: 2000, apr: 159, term: 12, status: "high-risk" as Recommendation },
  { lender: "Upstart", amount: 8000, apr: 14.5, term: 36, status: "safe" as Recommendation },
];
